import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles, Paper, Typography, Grid } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";

import API from "../../../utils/axios";
import { getHoursMins, timeToSeconds } from "../../../utils/helpers";
import Form from "../../common/Form";
import Button from "../../common/Button";
import { useSnackbar } from "notistack";

const useStyles = makeStyles(theme => ({
  cardContainer: {
    border: "1px solid #DDDDDD",
    padding: 24,
    marginBottom: 24,
    width: 696,
    maxWidth: 750,
    boxSizing: "border-box"
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: 16
  },
  cardText: {
    color: "#808080",
    fontSize: theme.typography.pxToRem(14)
  },
  btnSave: {
    marginTop: 8,
    textTransform: "none",
    boxShadow: "none !important",
    minWidth: 80,
    padding: theme.spacing(1.25, 2),
    lineHeight: "1.1rem",
    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: "#1329FE",
      color: "#fff",
      opacity: "38%"
    }
  }
}));

const initialFormValues = {
  startLocation: null,
  endLocationOption: "returnToStart",
  endLocation: null,
  startTime: new Date().setHours(8, 0),
  endTime: null
};

const DefaultVehicleSettingsCard = props => {
  const { setSaveDisabled, vehicleSettings, loadingSettings } = props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [inputValues, setInputValues] = useState(initialFormValues);
  const [errorValues, setErrorValues] = useState({
    startLocation: "",
    endLocationOption: "",
    endLocation: "",
    startTime: "",
    endTime: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  // Callbacks
  const updateInputValues = useCallback(
    (fieldName, fieldValue) => {
      setInputValues(previousInputValues => ({
        ...previousInputValues,
        [fieldName]: fieldValue
      }));
      setSaveDisabled(false);
      setFormChanged(true);
    },
    [setSaveDisabled]
  );

  const handleAddressSelect = useCallback(
    (field, value) => {
      if (value && value.place_id) {
        const service = new window.google.maps.places.PlacesService(
          window.document.createElement("div")
        );
        service.getDetails(
          {
            placeId: value.place_id,
            language: "en",
            fields: ["address_components", "geometry", "formatted_address"]
          },
          function (PlaceResult) {
            updateInputValues(field, PlaceResult);
          }
        );
      } else {
        updateInputValues(field, value);
      }
    },
    [updateInputValues]
  );

  const isValid = (name, defaultVal) => {
    if (!inputValues[name]) {
      updateInputValues(name, defaultVal);
      setErrorValues(previousErrorValues => ({
        ...previousErrorValues,
        previousErrorValues: "",
        [name]: ""
      }));
      return false;
    }
    if (inputValues[name].toString() === "Invalid Date") {
      setErrorValues(previousErrorValues => ({
        ...previousErrorValues,
        [name]: "Invalid time format"
      }));
      return false;
    }
    setErrorValues(previousErrorValues => ({
      ...previousErrorValues,
      [name]: ""
    }));
    return true;
  };

  const timeBetween = (start, end) => {
    if (
      inputValues[start]?.toString() !== "Invalid Date" &&
      inputValues[end]?.toString() !== "Invalid Date"
    )
      return setErrorValues(previousErrorValues => ({
        ...previousErrorValues,
        [start]:
          previousErrorValues[start] === "Invalid time format"
            ? previousErrorValues[start]
            : getHoursMins(inputValues[end]) <= getHoursMins(inputValues[start])
            ? "Invalid time window"
            : ""
      }));
  };

  const handleSubmit = () => {
    const errors = {
      startLocation: !inputValues.startLocation
        ? "Start location cannot be empty."
        : "",
      endLocation:
        inputValues.endLocationOption === "otherLocation" &&
        !inputValues.endLocation
          ? "End location cannot be empty."
          : ""
    };
    setErrorValues(previousErrorValues => ({
      ...previousErrorValues,
      ...errors
    }));

    if (Object.values(errors).filter(Boolean).length === 0) {
      setSubmitting(true);
      const data = {
        start_address: inputValues.startLocation,
        earliest_start: timeToSeconds(getHoursMins(inputValues.startTime)),
        latest_end: null,
        end_address: null
      };

      if (inputValues.endLocationOption === "returnToStart") {
        data.end_address = inputValues.startLocation;
      } else if (inputValues.endLocationOption === "otherLocation") {
        data.end_address = inputValues.endLocation;
      }

      if (inputValues.endTime) {
        data.latest_end = timeToSeconds(getHoursMins(inputValues.endTime));
      }
      API.put("/v1/settings/vehicles", data)
        .then(() => {
          setSaveDisabled(true);
          setFormChanged(false);
          enqueueSnackbar("Default vehicle settings updated.");
        })
        .catch(error => {
          enqueueSnackbar(
            "Something went wrong during updating default vehicle settings."
          );
          console.log(error);
        })
        .finally(() => setSubmitting(false));
    }
  };

  // Memos
  const firstFormData = useMemo(
    () => [
      [
        {
          disabled: submitting,
          renderInputField: true,
          label: "Start location",
          name: "startLocation",
          componentType: "vehicleAutoComplete",
          width: 382,
          noMarginTop: true,
          handleAddressSelect: value => {
            handleAddressSelect("startLocation", value);
            if (value) {
              setErrorValues(previousErrors => ({
                ...previousErrors,
                startLocation: ""
              }));
            }
          }
        }
      ],
      [
        {
          disabled: submitting,
          componentType: "select",
          label: "End location",
          name: "endLocationOption",
          showRadioButton: true,
          options: [
            [
              "returnToStart",
              "Return to start location",
              "Roundtrip (recommended)"
            ],
            [
              "otherLocation",
              "End routes at other location",
              "Add any end location"
            ],
            ["dontUse", "Don’t use end location", "Not recommended"]
          ],
          shrink: true,
          showOnlyOptionLabel: true,
          onChange: event => {
            updateInputValues("endLocationOption", event.target.value);
          }
        }
      ]
    ],
    [handleAddressSelect, updateInputValues]
  );

  const secondFormData = useMemo(
    () => [
      [
        {
          disabled: submitting,
          renderInputField: true,
          label: "Add any end location",
          name: "endLocation",
          width: 382,
          noMarginTop: true,
          componentType: "vehicleAutoComplete",
          handleAddressSelect: val => {
            handleAddressSelect("endLocation", val);
            if (val) {
              setErrorValues(previousErrors => ({
                ...previousErrors,
                endLocation: ""
              }));
            }
          }
        }
      ]
    ],
    [handleAddressSelect]
  );

  const thirdFormData = [
    [
      {
        disabled: submitting,
        componentType: "timeWindow",
        startLabel: "Start time",
        endLabel: "End time",
        startName: "startTime",
        endName: "endTime",
        onBlurStart: () => {
          isValid("startTime", new Date().setHours(8, 0)) &&
            timeBetween("startTime", "endTime");
        },
        onBlurEnd: () => {
          isValid("endTime", null) && timeBetween("startTime", "endTime");
        },
        onChangeStart: value => {
          updateInputValues("startTime", value);
        },
        onChangeEnd: value => {
          updateInputValues("endTime", value);
        }
      }
    ]
  ];

  // Effects
  useEffect(() => {
    if (vehicleSettings !== null) {
      setInputValues(vehicleSettings);
    }
  }, [vehicleSettings]);

  return (
    <Paper elevation={0} className={classes.cardContainer}>
      <Typography className={classes.cardTitle}>
        Default start location
      </Typography>
      <Typography className={classes.cardText}>
        Set the default start and end location for all vehicles. You can
        override this for specific vehicles from within each project.
      </Typography>
      {loadingSettings && (
        <Grid container direction="column">
          <Grid item>
            <Skeleton height={70} width={382} />
          </Grid>
          <Grid item>
            <Skeleton height={70} width={382} />
          </Grid>
          <Grid item container>
            <Skeleton height={70} width={181} />
            <Skeleton height={70} width={181} style={{ marginLeft: 20 }} />
          </Grid>
          <Grid item>
            <Skeleton height={52} width={80} />
          </Grid>
        </Grid>
      )}
      {!loadingSettings && (
        <div style={{ maxWidth: 382, marginTop: 24 }}>
          <Form
            data={firstFormData}
            inputValues={inputValues}
            errorValues={errorValues}
          />
          {inputValues.endLocationOption === "otherLocation" && (
            <Form
              data={secondFormData}
              inputValues={inputValues}
              errorValues={errorValues}
            />
          )}
          <Form
            data={thirdFormData}
            inputValues={inputValues}
            errorValues={errorValues}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!formChanged || submitting}
            className={classes.btnSave}
            onClick={handleSubmit}
            loading={submitting}
            progressSize={18}
          >
            Update
          </Button>
        </div>
      )}
    </Paper>
  );
};

export default DefaultVehicleSettingsCard;
